<template>
  <card-component
    class="resource-card"
    :size="cardSize"
    :class="cssClass"
    :aria-label="ariaLabelConfig?.cardComponent"
    @on-click="handlePrimaryCardAction"
    @keydown.enter="handlePrimaryCardAction"
  >
    <template #label>
      <label-component :text="locationLabel" :kind="labelKind" :class="labelCssClass" />
    </template>

    <template v-if="thumbnail" #thumbnail>
      <span class="icon">
        <svg-icon v-if="thumbnail.type === 'icon'" :name="thumbnail.value" kind="indicative" />
        <img v-else-if="thumbnail.type === 'image'" :src="getIconUrlWithDate()" />
        <component :is="thumbnail.value" v-else-if="thumbnail.type === 'svg'" class="thumbnail" />
      </span>
    </template>

    <template #header>
      <h6 class="title">{{ title }}</h6>
    </template>
    <template #metadata>
      <li class="info">
        <span v-if="metadata">{{ metadata }}</span>
        <span v-if="showSubjectMetaData" class="subject">{{ subjectLabel }}</span>
      </li>
    </template>
    <template v-if="showCardTooltipMessage" #content>
      <cv-tooltip alignment="center" direction="bottom" :tip="cardTooltipMessage" />
    </template>
    <template #actions>
      <cv-button
        v-if="showViewDetails"
        class="icon-button"
        kind="ghost"
        size="sm"
        :aria-label="ariaLabelConfig?.viewDetails"
        tabindex="-1"
        @click="handleViewDetails"
      >
        <svg-icon name="Details24" kind="action" />
      </cv-button>
      <cv-button
        v-if="showEmbed"
        class="icon-button"
        kind="ghost"
        size="sm"
        tabindex="-1"
        @click="handleEmbed"
      >
        <svg-icon name="Embed24" kind="action" />
      </cv-button>
      <input-checkbox
        v-if="selectionMode === 'multiple'"
        :id="uuid"
        value="true"
        kind="white"
        :selected-values="[String(selected)]"
        @change="handleSelect"
      />
    </template>
  </card-component>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ResourceCardUtils from "@utils/resource.card.utils.ts";
import { ResourceCardProps } from "@/common/card-types.ts";
import {
  getLocationLabel,
  RESOURCE_SEARCH_TYPE,
} from "@modules/resource-search/resource-search-helper.ts";
import { ResourceSearchType } from "@/modules/resource-search/resource-search-types";
import { isEmpty } from "@utils/var.check";
import { formatSubjectTitleCase } from "@utils/subject.utils.ts";

// Props
const props = withDefaults(defineProps<ResourceCardProps>(), {
  uuid: "",
  subjectLevelList: () => [],
  cardSize: "xsmall",
  showViewDetails: false,
  showEmbed: false,
});

interface Emits {
  (event: "click"): void;

  (event: "view-details"): void;

  (event: "select", uuid: string): void;

  (event: "embed"): void;
}

const emit = defineEmits<Emits>();

//composables
const { t } = useI18n();

// Constants / Lookup Maps
const searchTypeCssClassMap: Partial<Record<ResourceSearchType, string>> = {
  [RESOURCE_SEARCH_TYPE.APP]: "app-card",
  [RESOURCE_SEARCH_TYPE.MEDIA]: "media-card",
  [RESOURCE_SEARCH_TYPE.QUESTION]: "question-card",
  [RESOURCE_SEARCH_TYPE.WEBSITE]: "website-card",
  [RESOURCE_SEARCH_TYPE.RUBRIC]: "rubric-card",
};

// Computed
const showSubjectMetaData = computed(() => {
  return !isEmpty(subjectLabel.value);
});

const subjectLabel = computed(() => {
  if (isEmpty(props.subjectLevelList)) {
    return "";
  }
  return formatSubjectTitleCase(props.subjectLevelList[0].subjectName);
});

const labelKind = computed(() => {
  return ResourceCardUtils.getLabelKind(props.searchType);
});

const labelCssClass = computed(() => {
  return ResourceCardUtils.getLabelCssClasses(props.resourceDomain);
});

const cssClass = computed(() => [
  {
    "has-thumbnail": props.thumbnail?.type === "image",
    _disabled: props.disabled,
  },
  searchTypeCssClassMap[props.searchType],
]);

const cardTooltipMessage = computed(() => {
  if (props.showCardTooltipMessage) {
    return t("app_disabled_tooltip_message");
  }
  return "";
});

const locationLabel = computed((): string => {
  return getLocationLabel(props.resourceDomain, props.searchType);
});

// Methods
const handlePrimaryCardAction = () => {
  if (props.selectionMode) {
    handleSelect();
    return;
  }

  emit("click");
};

const handleViewDetails = () => {
  emit("view-details");
};

const handleEmbed = () => {
  emit("embed");
};

const handleSelect = () => {
  emit("select", props.uuid);
};

const getIconUrlWithDate = (): string => {
  if (props.thumbnail?.type !== "image") {
    return "";
  }

  return `${props.thumbnail?.value || ""}?t=${Date.now()}`;
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.card-component.resource-card {
  .card-label {
    background-color: transparent;
    padding: 0;
  }

  .card-header {
    padding-top: $spacing-03;
    .title {
      @include carbon--type-style("card-title-02");
    }
  }

  .card-thumbnail {
    .icon {
      background-color: $ui-01;
      border-radius: 50%;
    }
  }

  .input-checkbox {
    margin: 0;

    .input-icon {
      margin: 0;
    }
  }

  .card-actions {
    right: 0.5rem;
  }

  .label-component {
    span {
      @include carbon--type-style("card-label-01");
      padding: $spacing-01 $spacing-03;
      text-transform: uppercase;
      color: $text-04;
      border-radius: 5px;
    }

    //website
    &.external-resource {
      span {
        background-color: $poll-10;
      }
    }

    //app
    &.location {
      span {
        background-color: $location-04;
      }
    }

    //location - moe
    &.moe {
      span {
        background-color: $location-01;
      }
    }

    //location - commnunity
    &.community {
      span {
        background-color: $location-02;
      }
    }
  }

  .info {
    @include line-clamp(1);
    word-break: break-all;

    span {
      position: relative;
      word-break: break-word;

      + span {
        padding-left: 9px;

        &::before {
          content: "";
          // https://stackoverflow.com/a/49783868/21957255
          // Display inline block is needed to fix text capitalize issue
          display: inline-block;
          height: 2px;
          width: 2px;
          position: absolute;
          left: 3px;
          top: 8px;
          border-radius: 50%;
          background-color: $text-02;
        }
      }
    }

    .subject {
      text-transform: capitalize;
    }
  }
}
</style>
